import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Plus, Search, Filter, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { TIPI_DOCUMENTO, STATO_BADGE } from "@/lib/constants";

export default function Documents() {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const isConsultant = user?.role === "consultant";
  const [docs, setDocs] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [q, setQ] = useState("");
  const [fProcess, setFProcess] = useState("all");
  const [fTipo, setFTipo] = useState("all");
  const [sel, setSel] = useState(new Set());

  const toggleRow = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDelete = async () => {
    try {
      const r = await api.post(`/documents/bulk-delete`, { ids: [...sel] });
      toast.success(`${r.data.deleted} documenti eliminati`);
      setSel(new Set()); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = () => {
    const params = new URLSearchParams();
    if (tenantId) params.set("tenant_id", tenantId);
    if (fProcess !== "all") params.set("process_id", fProcess);
    if (fTipo !== "all") params.set("tipo", fTipo);
    api.get(`/documents?${params.toString()}`).then((r) => { setDocs(r.data); setSel(new Set()); });
  };
  useEffect(() => {
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/processes${p}`).then((r) => setProcesses(r.data));
  }, [tenantId]);
  useEffect(load, [tenantId, fProcess, fTipo]);

  const del = async (id, codice) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success(`Documento ${codice} eliminato`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = docs.filter((d) =>
    !q || `${d.codice} ${d.titolo}`.toLowerCase().includes(q.toLowerCase())
  );
  const allChecked = filtered.length > 0 && filtered.every((d) => sel.has(d.id));
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(filtered.map((d) => d.id)));

  return (
    <div className="space-y-6">
      <PageHero label="Archivio" title="Documenti">
        {isConsultant && (
          <>
            <Link to="/documenti/importa">
              <Button variant="outline" data-testid="bulk-import-btn">
                <Plus className="h-4 w-4 mr-2" /> Import ZIP/multi
              </Button>
            </Link>
            <Link to="/documenti/nuovo">
              <Button data-testid="new-document-btn">
                <Plus className="h-4 w-4 mr-2" /> Carica e scheda
              </Button>
            </Link>
          </>
        )}
      </PageHero>

      {isConsultant && !tenantId && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-800">
          Stai visualizzando i documenti di <strong>tutti i clienti</strong>. Seleziona un cliente in alto per filtrare.
        </Card>
      )}

      <Card className="border-slate-200">
        {isConsultant && filtered.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-blue-50 border-b border-blue-200" data-testid="bulk-bar">
            <label className="flex items-center gap-2 text-sm font-medium text-blue-800 cursor-pointer select-none" data-testid="select-all-bar">
              <Checkbox checked={allChecked} onCheckedChange={toggleAll} />
              Seleziona tutto{sel.size > 0 ? ` — ${sel.size} selezionati` : ""}
            </label>
            {sel.size > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive" data-testid="bulk-delete-btn" className="bg-red-600 hover:bg-red-700">
                  <Trash2 className="h-4 w-4 mr-2" /> Elimina selezionati
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="bulk-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare {sel.size} documenti?</AlertDialogTitle>
                  <AlertDialogDescription>
                    I documenti selezionati verranno eliminati definitivamente insieme ai file caricati. L'azione non è reversibile.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="bulk-cancel">Annulla</AlertDialogCancel>
                  <AlertDialogAction data-testid="bulk-confirm" onClick={bulkDelete} className="bg-red-600 hover:bg-red-700">Elimina tutti</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-200">
          <div className="relative flex-1 min-w-full sm:min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input placeholder="Cerca per codice o titolo..." value={q} data-testid="doc-search"
              onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={fProcess} onValueChange={setFProcess}>
            <SelectTrigger className="w-full sm:w-52" data-testid="filter-process"><SelectValue placeholder="Processo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i processi</SelectItem>
              {processes.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fTipo} onValueChange={setFTipo}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {TIPI_DOCUMENTO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nessun documento trovato.</p>
          </div>
        ) : (
          <>
          {/* Mobile: schede impilate */}
          <div className="md:hidden divide-y divide-slate-100" data-testid="docs-cards-mobile">
            {filtered.map((d) => (
              <div key={d.id} data-testid={`doc-card-${d.id}`} className="p-4 flex items-start gap-3">
                {isConsultant && (
                  <Checkbox className="mt-1" checked={sel.has(d.id)} onCheckedChange={() => toggleRow(d.id)} data-testid={`select-doc-m-${d.id}`} />
                )}
                <Link to={`/documenti/${d.id}`} className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] text-blue-700">{d.codice || "—"}</div>
                  <div className="text-sm font-medium leading-snug mt-0.5">{d.titolo}</div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <Badge variant="outline" className="text-[11px]">{d.tipo}</Badge>
                    <Badge className={`text-[11px] ${STATO_BADGE[d.stato] || ""}`}>{d.stato}</Badge>
                    <span className="text-[11px] text-slate-500 font-mono">v{d.versione}</span>
                    {d.data_prossima_revisione && (
                      <span className="text-[11px] text-slate-500 font-mono">· rev. {d.data_prossima_revisione}</span>
                    )}
                  </div>
                </Link>
                {isConsultant && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`delete-doc-m-${d.id}`}
                        className="h-9 w-9 shrink-0 text-slate-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare il documento?</AlertDialogTitle>
                        <AlertDialogDescription>
                          «{d.codice} — {d.titolo}» verrà eliminato definitivamente insieme al file caricato. L'azione non è reversibile.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(d.id, d.codice)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            ))}
          </div>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                {isConsultant && <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all-docs" /></TableHead>}
                <TableHead className="w-28">Codice</TableHead>
                <TableHead>Titolo</TableHead>
                <TableHead className="w-24">Tipo</TableHead>
                <TableHead className="w-24">Versione</TableHead>
                <TableHead className="w-28">Stato</TableHead>
                <TableHead className="w-32">Prox. revisione</TableHead>
                {isConsultant && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`} className="cursor-pointer">
                  {isConsultant && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={sel.has(d.id)} onCheckedChange={() => toggleRow(d.id)} data-testid={`select-doc-${d.id}`} />
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-sm">
                    <Link to={`/documenti/${d.id}`} className="text-blue-700 hover:underline">{d.codice}</Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/documenti/${d.id}`} className="hover:underline">{d.titolo}</Link>
                  </TableCell>
                  <TableCell><Badge variant="outline">{d.tipo}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-500">{d.versione}</TableCell>
                  <TableCell><Badge className={STATO_BADGE[d.stato] || ""}>{d.stato}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-500">{d.data_prossima_revisione || "—"}</TableCell>
                  {isConsultant && (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`delete-doc-${d.id}`}
                            className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="delete-doc-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare il documento?</AlertDialogTitle>
                            <AlertDialogDescription>
                              «{d.codice} — {d.titolo}» verrà eliminato definitivamente insieme al file caricato. L'azione non è reversibile.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="cancel-delete">Annulla</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-${d.id}`}
                              onClick={() => del(d.id, d.codice)}
                              className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          </>
        )}
      </Card>
    </div>
  );
}
